import type { CatalogImportCode } from '@/api/handlers/resources/catalogImports';
import type {
    CatalogImportLineResult,
    CatalogImportNameResult,
} from '@/api/models';
import { targetOfKey, type NameOption } from '@/pages/catalogImport/names';
import { comparableText } from '@/pages/catalogImport/parse';
import {
    bulkFixGroupOf,
    type BulkFixSource,
    type ParsedLine,
    type VariantMismatchField,
} from '@/pages/catalogImport/store';
import {
    entityKeyOf,
    unitKeyOf,
    vatKeyOf,
    type RowDrawerContext,
    type RowDrawerField,
} from './rowDrawerForm';

/**
 * What the problem card offers beyond its sentences: the catalog variants an
 * ambiguous row could mean, the variant the user picked for it, the values
 * of a new product's rows that disagree, a variant's differing figures to
 * pick one of, the barcodes a variant keeps, and the IMEI move or shared
 * barcode fix for the row's group. Pure; `useRowDrawer` acts on them.
 */

const GROUP_FIELDS_DIFFER = 'GROUP_FIELDS_DIFFER' satisfies CatalogImportCode;
const SKU_BARCODE_CONFLICT = 'SKU_BARCODE_CONFLICT' satisfies CatalogImportCode;
const VARIANT_FIELDS_DIFFER =
    'VARIANT_FIELDS_DIFFER' satisfies CatalogImportCode;
const BARCODES_KEPT_AS_ALTERNATES =
    'BARCODES_KEPT_AS_ALTERNATES' satisfies CatalogImportCode;
const BARCODE_LOOKS_LIKE_IMEI =
    'BARCODE_LOOKS_LIKE_IMEI' satisfies CatalogImportCode;
const BARCODE_SHARED_ACROSS_SKUS =
    'BARCODE_SHARED_ACROSS_SKUS' satisfies CatalogImportCode;

const trimmed = (value: string | null | undefined): string =>
    value?.trim() ?? '';

// ---- Candidates and the pick -----------------------------------------------------

/**
 * One catalog variant the row could mean, as the card shows it. The server
 * sends candidates with `BARCODE_AMBIGUOUS` (the barcode names variants of
 * several products) and `SKU_BARCODE_CONFLICT` (the SKU names one product,
 * the barcode another).
 */
export type RowDrawerCandidate = {
    variantId: string;
    productName: string;
    /**
     * The row's SKU when the candidate is the SKU's product: a candidate
     * carries no SKU of its own, so the barcode's products show none.
     */
    sku: string | null;
    barcode: string | null;
    /** Its option value names, in the product's axis order. */
    attributes: string[];
    /** Units on hand in the import's branch. */
    stock: number;
    salePriceTetri: number | null;
    /**
     * On a `SKU_BARCODE_CONFLICT`, what ties it to the row: the SKU or the
     * barcode. `null` on `BARCODE_AMBIGUOUS`, where every candidate has the
     * barcode.
     */
    matchedBy: 'sku' | 'barcode' | null;
};

/** The variant the user picked: the row is an explicit restock of it. */
export type RowDrawerPickedVariant = {
    variantId: string;
    /** What the card showed for it; `null` when the drawer no longer knows (the step was left and opened again). */
    candidate: RowDrawerCandidate | null;
};

/** The variant the line restocks by the user's pick (its `variantId`), or `null`. */
export const pickedVariantIdOf = (line: ParsedLine): string | null => {
    const variantId = trimmed(line.variantId);
    return variantId === '' ? null : variantId;
};

/**
 * The last check's candidates for the row, once each. A candidate whose
 * barcode is the row's (the SKU's own when the row has none, as the server
 * reads it) matched by the barcode; on a conflict, the others are the SKU's
 * product. A candidate without a variant cannot be picked and is left out.
 */
export const candidatesOf = (
    line: ParsedLine,
    result: CatalogImportLineResult | null,
): RowDrawerCandidate[] => {
    if (!result) {
        return [];
    }
    const isConflict = result.problems.some(
        (problem) => problem.code === SKU_BARCODE_CONFLICT,
    );
    const sku = trimmed(line.sku);
    const rowBarcode = comparableText(trimmed(line.barcode) || sku);
    const seen = new Set<string>();
    const candidates: RowDrawerCandidate[] = [];
    for (const candidate of result.candidates) {
        const variantId = trimmed(candidate.variantId);
        if (variantId === '' || seen.has(variantId)) {
            continue;
        }
        seen.add(variantId);
        const byBarcode =
            rowBarcode !== '' &&
            comparableText(candidate.barcode ?? '') === rowBarcode;
        const matchedBy = isConflict ? (byBarcode ? 'barcode' : 'sku') : null;
        candidates.push({
            variantId,
            productName: candidate.productName,
            sku: matchedBy === 'sku' && sku !== '' ? sku : null,
            barcode: trimmed(candidate.barcode) || null,
            attributes: candidate.attributes,
            stock: candidate.stock,
            salePriceTetri: candidate.salePriceTetri,
            matchedBy,
        });
    }
    return candidates;
};

// ---- Group mismatch ----------------------------------------------------------------

/**
 * The product fields the server compares across the rows of one new SKU
 * (`GROUP_FIELDS_DIFFER`'s `fields`), in the form's order.
 */
export const GROUP_MISMATCH_FIELDS = [
    'name',
    'category',
    'brand',
    'unit',
    'vatType',
] as const;

export type GroupMismatchField = (typeof GROUP_MISMATCH_FIELDS)[number];

/** The form field showing each, so a taken value replaces an edit of it. */
export const FORM_FIELD_OF = {
    name: 'name',
    category: 'categoryKey',
    brand: 'brandKey',
    unit: 'unit',
    vatType: 'vatType',
} as const satisfies Record<GroupMismatchField, RowDrawerField>;

const listedFields = (
    problem: CatalogImportLineResult['problems'][number],
): readonly unknown[] => {
    if (problem.fields && problem.fields.length > 0) {
        return problem.fields;
    }
    const listed = problem.params?.fields;
    return Array.isArray(listed) ? listed : [];
};

/** The fields `GROUP_FIELDS_DIFFER` names on the row; none without that problem. */
export const groupMismatchFieldsOf = (
    result: CatalogImportLineResult | null,
): GroupMismatchField[] => {
    const problem = result?.problems.find(
        (candidate) => candidate.code === GROUP_FIELDS_DIFFER,
    );
    if (!problem) {
        return [];
    }
    const listed = listedFields(problem);
    return GROUP_MISMATCH_FIELDS.filter((field) => listed.includes(field));
};

/**
 * One row's value for a differing field, as the card shows it: the name as
 * written; a category or brand by the catalog's name for it (what the last
 * check resolved the text to, as the form shows it), else its text; the unit
 * and VAT as the selects' keys (`Piece`, `standard`, or the text the file
 * has), which the View names. Blank is `''`.
 */
export type RowDrawerGroupValue = {
    field: GroupMismatchField;
    value: string;
};

/** One row of the group, with its values for the differing fields. */
export type RowDrawerGroupRow = {
    rowNumber: number;
    /** The row the drawer shows. */
    isCurrent: boolean;
    values: RowDrawerGroupValue[];
};

export type RowDrawerGroupMismatch = {
    fields: GroupMismatchField[];
    rows: RowDrawerGroupRow[];
    /**
     * The row whose values the bulk fix would give the SKU (the first by
     * number that fills in every differing field recognisably, the user's
     * excluded rows aside), marked Suggested; `null` when the store finds no
     * group or the fields come from different rows.
     */
    suggestedRow: number | null;
};

/** The catalog's categories and brands, by id, for naming a row's value. */
export type RowDrawerGroupCatalog = {
    categories: readonly NameOption[];
    brands: readonly NameOption[];
};

const entityValue = (
    id: string | null | undefined,
    text: string | null | undefined,
    entries: readonly CatalogImportNameResult[],
    options: readonly NameOption[],
): string => {
    const key = entityKeyOf(id, text, entries);
    if (key === null) {
        return '';
    }
    const option = options.find((candidate) => candidate.id === key);
    if (option) {
        return option.label;
    }
    const target = targetOfKey(key);
    if (target !== null && 'text' in target) {
        return target.text;
    }
    return trimmed(text) || key;
};

const groupValueOf = (
    line: ParsedLine,
    field: GroupMismatchField,
    context: RowDrawerContext,
    catalog: RowDrawerGroupCatalog,
): string => {
    switch (field) {
        case 'name':
            return trimmed(line.name);
        case 'category':
            return entityValue(
                line.categoryId,
                line.categoryText,
                context.names?.categories ?? [],
                catalog.categories,
            );
        case 'brand':
            return entityValue(
                line.brandId,
                line.brandText,
                context.names?.brands ?? [],
                catalog.brands,
            );
        case 'unit':
            return unitKeyOf(line.unit);
        case 'vatType':
            return vatKeyOf(line.vatType);
    }
};

/**
 * The group's rows with their values for the fields `GROUP_FIELDS_DIFFER`
 * names on the row, in row order; `null` without that problem, or when the
 * store no longer holds a second row of the group.
 */
export const groupMismatchOf = (
    rowNumber: number,
    result: CatalogImportLineResult | null,
    groupLines: readonly ParsedLine[],
    context: RowDrawerContext,
    catalog: RowDrawerGroupCatalog,
    suggestedRow: number | null = null,
): RowDrawerGroupMismatch | null => {
    const fields = groupMismatchFieldsOf(result);
    if (fields.length === 0 || groupLines.length < 2) {
        return null;
    }
    return {
        fields,
        suggestedRow,
        rows: [...groupLines]
            .sort((a, b) => a.rowNumber - b.rowNumber)
            .map((line) => ({
                rowNumber: line.rowNumber,
                isCurrent: line.rowNumber === rowNumber,
                values: fields.map((field) => ({
                    field,
                    value: groupValueOf(line, field, context, catalog),
                })),
            })),
    };
};

// ---- Variant figures --------------------------------------------------------------

/** One value of a differing figure, with the rows that carry it. */
export type RowDrawerVariantValue = {
    /** Decimal GEL for a price, a count for the minimum; `null` is a blank cell. */
    value: number | null;
    rows: number[];
    /** The bulk fix's default: the most common number, the higher on a tie. */
    isSuggested: boolean;
};

/** One figure `VARIANT_FIELDS_DIFFER` names, as the card offers it. */
export type RowDrawerVariantFigure = {
    field: VariantMismatchField;
    /** The most common number first (the higher on a tie), a blank last. */
    values: RowDrawerVariantValue[];
    /** The value the radios show picked: the user's, else the suggested one. */
    picked: number | null;
};

/** A variant's differing figures, over the rows `params.rows` names. */
export type RowDrawerVariantFixModel = {
    /** The variant's rows the user has not excluded, in row order. */
    rows: number[];
    figures: RowDrawerVariantFigure[];
};

/** The user's pick per figure, while the drawer shows the row. */
export type RowDrawerVariantPicks = Partial<
    Record<VariantMismatchField, number | null>
>;

/**
 * The row's variant figures to pick from: the store's group for the row's
 * `VARIANT_FIELDS_DIFFER` (its `choices`, #217), each value with its rows
 * and the default marked. `null` without that problem on the row, when the
 * row is excluded, or when the store finds no figure with two values. A
 * pick the figure no longer has falls back to the suggested value.
 */
export const variantFixOf = (
    source: BulkFixSource,
    rowNumber: number,
    result: CatalogImportLineResult | null,
    picks: RowDrawerVariantPicks,
): RowDrawerVariantFixModel | null => {
    if (
        !result?.problems.some(
            (problem) => problem.code === VARIANT_FIELDS_DIFFER,
        )
    ) {
        return null;
    }
    const group = bulkFixGroupOf(source, VARIANT_FIELDS_DIFFER, rowNumber);
    const choices = (group?.choices ?? []).filter(
        (choice) => choice.values.length > 1,
    );
    if (!group || choices.length === 0) {
        return null;
    }
    return {
        rows: group.rows,
        figures: choices.map((choice) => {
            const hasValue = (value: number | null | undefined) =>
                value !== undefined &&
                choice.values.some((entry) => entry.value === value);
            const own = picks[choice.field];
            const suggested = hasValue(choice.suggested)
                ? choice.suggested
                : (choice.values[0]?.value ?? null);
            return {
                field: choice.field,
                values: choice.values.map((entry) => ({
                    value: entry.value,
                    rows: entry.rows,
                    isSuggested:
                        choice.suggested !== null &&
                        entry.value === choice.suggested,
                })),
                picked: hasValue(own) ? (own ?? null) : suggested,
            };
        }),
    };
};

// ---- Barcodes kept as alternates ----------------------------------------------------

/** A barcode the variant keeps besides its main one, with the row to make it main from. */
export type RowDrawerExtraBarcode = {
    barcode: string;
    /** The first of the variant's rows carrying it; `null` when no line does any more. */
    rowNumber: number | null;
};

/** `BARCODES_KEPT_AS_ALTERNATES` on the row: the variant's main and extra barcodes. */
export type RowDrawerBarcodesModel = {
    main: string;
    /** The main one is the user's Make main (`primaryBarcode`), not the most stock. */
    isPicked: boolean;
    extras: RowDrawerExtraBarcode[];
};

const barcodeKey = (value: string | null | undefined): string =>
    comparableText(value ?? '');

/**
 * The barcodes the last check kept for the row's new variant: the main one
 * (`params.primary`) and the extras (`params.alternates`), each extra with
 * the first row of the variant (`params.rows`) whose barcode it is, so Make
 * main can flag that line. Codes compare trimmed and case-insensitively, as
 * the import matches them. `null` without the warning.
 */
export const barcodesOf = (
    result: CatalogImportLineResult | null,
    lines: readonly ParsedLine[],
): RowDrawerBarcodesModel | null => {
    const warning = result?.warnings.find(
        (candidate) => candidate.code === BARCODES_KEPT_AS_ALTERNATES,
    );
    const primary = warning?.params?.primary;
    if (!result || typeof primary !== 'string') {
        return null;
    }
    const listed = warning?.params?.rows;
    const rows = new Set(
        [result.rowNumber, ...(Array.isArray(listed) ? listed : [])].filter(
            (row): row is number => Number.isInteger(row),
        ),
    );
    const variantLines = lines
        .filter((line) => rows.has(line.rowNumber))
        .sort((a, b) => a.rowNumber - b.rowNumber);
    const alternates = warning?.params?.alternates;
    return {
        main: primary,
        isPicked: variantLines.some(
            (line) =>
                line.primaryBarcode === true &&
                barcodeKey(line.barcode) === barcodeKey(primary),
        ),
        extras: (Array.isArray(alternates) ? alternates : [])
            .filter((code): code is string => typeof code === 'string')
            .map((barcode) => ({
                barcode,
                rowNumber:
                    variantLines.find(
                        (line) =>
                            barcodeKey(line.barcode) === barcodeKey(barcode),
                    )?.rowNumber ?? null,
            })),
    };
};

// ---- Barcode fixes for one group --------------------------------------------------

/** The barcode fixes the drawer offers for the row's group alone. */
export type RowDrawerBarcodeFixCode =
    typeof BARCODE_LOOKS_LIKE_IMEI | typeof BARCODE_SHARED_ACROSS_SKUS;

/**
 * A barcode fix for the row's group, as the problem card offers it: the
 * IMEI move (the barcode goes to Lot number), or the shared barcode kept
 * on the first SKU by row (`sku`) and cleared on the others' rows.
 */
export type RowDrawerBarcodeFixModel =
    | { code: typeof BARCODE_LOOKS_LIKE_IMEI; barcode: string }
    | {
          code: typeof BARCODE_SHARED_ACROSS_SKUS;
          barcode: string;
          /** The SKU that keeps the barcode: the group's first row's. */
          sku: string;
      };

/**
 * The barcode fixes of the row's problems (bos-app#213, US-07): the store's
 * group for the row's `BARCODE_LOOKS_LIKE_IMEI` and `BARCODE_SHARED_ACROSS_SKUS`
 * (#217), the IMEI move first. A kind is left out without that problem on
 * the row, when the row is excluded, or when the default would change no
 * row (an edit since the check answered it). The barcode is the problem's
 * `params.barcode`, else the row's.
 */
export const barcodeFixesOf = (
    source: BulkFixSource,
    rowNumber: number,
    result: CatalogImportLineResult | null,
): RowDrawerBarcodeFixModel[] => {
    const fixes: RowDrawerBarcodeFixModel[] = [];
    for (const code of [
        BARCODE_LOOKS_LIKE_IMEI,
        BARCODE_SHARED_ACROSS_SKUS,
    ] as const) {
        const problem = result?.problems.find(
            (candidate) => candidate.code === code,
        );
        const group = problem ? bulkFixGroupOf(source, code, rowNumber) : null;
        if (!problem || !group || group.patches.size === 0) {
            continue;
        }
        const named = problem.params?.barcode;
        const lineOf = (row: number | undefined) =>
            source.lines.find((line) => line.rowNumber === row);
        const barcode =
            typeof named === 'string'
                ? named
                : trimmed(lineOf(rowNumber)?.barcode);
        fixes.push(
            code === BARCODE_LOOKS_LIKE_IMEI
                ? { code, barcode }
                : { code, barcode, sku: trimmed(lineOf(group.rows[0])?.sku) },
        );
    }
    return fixes;
};
